/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadExternalProviders from "@insite/client-framework/Store/Pages/SignIn/Handlers/LoadExternalProviders";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@rc/MobiusOverrides/Button/Button";
import { signInExternalProvidersHandler } from "@rc/XDL/Utilities/signInExternalProvidersHandler";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    currentUsersContent = "currentUsersContent",
    signInButtonText = "signInButtonText",
    newUsersContent = "newUsersContent",
    newUsersLinkText = "newUsersLinkText",
    newUsersLinkUrl = "newUsersLinkUrl",
    createOnlineAccessContent = "createOnlineAccessContent",
    createOnlineAccessLinkText = "createOnlineAccessLinkText",
    createOnlineAccessLinkUrl = "createOnlineAccessLinkUrl",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        externalProviders: state.pages.signIn.externalProviders,
        location: getLocation(state),
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    loadExternalProviders,
};

interface AnonymousLoginContentProps extends WidgetProps {
    fields: {
        [fields.currentUsersContent]: string;
        [fields.signInButtonText]: string;
        [fields.newUsersContent]: string;
        [fields.newUsersLinkText]: string;
        [fields.newUsersLinkUrl]: LinkFieldValue;
        [fields.createOnlineAccessContent]: string;
        [fields.createOnlineAccessLinkText]: string;
        [fields.createOnlineAccessLinkUrl]: LinkFieldValue;
    };
}

export interface AnonymousLoginContentStyles {
    container?: InjectableCss;
    top?: InjectableCss;
    center?: InjectableCss;
    bottom?: InjectableCss;
}

export const anonymousLoginContentStyles: AnonymousLoginContentStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 20px;
            border: 1px solid ${getColor("common.ui3")};

            @media (max-width: 1024px) and (min-width: 767px) {
                flex-direction: row;
                justify-content: space-between;
            }
        `,
    },
    top: {
        css: css`
            @media (max-width: 1024px) and (min-width: 767px) {
                width: 28%;
            }
        `,
    },
    center: {
        css: css`
            margin: 32px 0;

            @media (max-width: 1024px) and (min-width: 767px) {
                margin: 0 12px;
                width: 36%;
            }
        `,
    },
    bottom: {
        css: css`
            @media (max-width: 1024px) and (min-width: 767px) {
                width: 36%;
            }
        `,
    },
};

type Props = AnonymousLoginContentProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = anonymousLoginContentStyles;

export const AnonymousLoginContent = ({
    id,
    fields: {
        currentUsersContent,
        signInButtonText,
        newUsersContent,
        newUsersLinkText,
        newUsersLinkUrl,
        createOnlineAccessContent,
        createOnlineAccessLinkText,
        createOnlineAccessLinkUrl,
    },
    userName,
    currentUserIsGuest,
    externalProviders,
    signInUrl,
    location,
}: Props) => {
    const newUsersLinkUrlLink = useGetLink(newUsersLinkUrl);
    const createOnlineAccessLinkUrlLink = useGetLink(createOnlineAccessLinkUrl);

    // React.useEffect(() => {
    //     if (!externalProviders) {
    //         loadExternalProviders();
    //     }
    // }, [externalProviders]);

    const onSignInHandler = (e: React.MouseEvent) => {
        // https://xngage.atlassian.net/browse/RC-2277
        signInExternalProvidersHandler(e, undefined, userName, currentUserIsGuest, externalProviders, signInUrl, location);
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.top}>
                <Typography>{parse(currentUsersContent, parserOptions)}</Typography>
                <Button variant="primary" onClick={onSignInHandler} rel="noreferral noopener">
                    {signInButtonText}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.center}>
                <Typography>{parse(newUsersContent, parserOptions)}</Typography>
                <Link href={newUsersLinkUrlLink?.url} rel="noreferral noopener" color="primary.main">
                    {newUsersLinkText}
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.bottom}>
                <Typography>{parse(createOnlineAccessContent, parserOptions)}</Typography>
                <Link href={createOnlineAccessLinkUrlLink?.url} rel="noreferral noopener" color="primary.main">
                    {createOnlineAccessLinkText}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    fieldDefinitions: [
        {
            name: fields.currentUsersContent,
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.signInButtonText,
            editorTemplate: "TextField",
            defaultValue: "Sign In",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.newUsersContent,
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.newUsersLinkText,
            editorTemplate: "TextField",
            defaultValue: "Apply for an Account",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.newUsersLinkUrl,
            editorTemplate: "LinkField",
            defaultValue: {
                value: "",
                type: "Page",
            },
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.createOnlineAccessContent,
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.createOnlineAccessLinkText,
            editorTemplate: "TextField",
            defaultValue: "Create User",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.createOnlineAccessLinkUrl,
            editorTemplate: "LinkField",
            defaultValue: {
                value: "",
                type: "Page",
            },
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AnonymousLoginContent),
    definition,
};

export default widgetModule;
